import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getLocale, detectBrowserLanguage, SUPPORTED_LOCALES } from '../i18n';
import axios from 'axios';

const API = (typeof window !== 'undefined' && window.location?.origin
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + '/api';

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Priority: localStorage > browser detect > 'en'
    const stored = localStorage.getItem('language');
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
    return detectBrowserLanguage();
  });

  const locale = getLocale(language);

  useEffect(() => {
    localStorage.setItem('language', language);
    // Set document direction for RTL languages
    document.documentElement.dir = locale.dir || 'ltr';
    document.documentElement.lang = language;
  }, [language, locale.dir]);

  const setLanguage = useCallback(async (code) => {
    setLanguageState(code);
    // Persist to backend
    try {
      await axios.put(`${API}/users/me/language`, { language: code }, { withCredentials: true });
    } catch (error) {
      // Silently fail - local storage is the primary
    }
  }, []);

  // Helper function to get nested translation
  const t = useCallback((path, fallback) => {
    const keys = path.split('.');
    let result = locale;
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        return fallback || path;
      }
    }
    return typeof result === 'string' ? result : (fallback || path);
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, locale, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
